package com.example.mystybag.contacts.logic

import com.example.mystybag.core.extensions.deleteAt
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.extensions.equalsIgnoreCase
import com.example.mystybag.core.extensions.isValidPhone
import com.example.mystybag.core.extensions.normalizedPhoneNumber
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.database.model.PickContactDataModel
import com.example.mystybag.common.ui.components.custom.ItemDecor
import com.example.mystybag.contacts.helpers.ContactManager.getContacts
import com.example.mystybag.contacts.model.PickContactUIState
import com.example.mystybag.contacts.model.PickContactVisualModel
import com.example.mystybag.contacts.model.PickContactVisualModel.Companion.fromContactRowModel
import com.example.mystybag.permissionsUtils.expected.Permission
import com.example.mystybag.permissionsUtils.expected.PermissionHelper
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_ARE_CONTACTS_SYNCED
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

enum class PickContactSearchType {
    FIELD,
    NAVIGATION
}

enum class PickContactSelectedType {
    NONE,
    CHECKBOX,
    TICK
}

class PickContactViewModel(
    context                     : AppContext,
    private val searchType      : PickContactSearchType,
    private val onSuccess       : (List<PickContactDataModel>) -> Boolean = { true },
    private val onQRCodeClick   : (() -> Unit)? = null
): ChildViewModel<Unit>(
    context         = context,
    uiData          = Unit
) {

    private var contactsJob             : Job?                              = null
    private var searchJob               : Job?                              = null
    private var mutex                                                       = Mutex()
    private var contactsDataModelList                                       = arrayListOf<PickContactDataModel>()
    private var checkedIdentifiers      : MutableList<PickContactDataModel> = mutableListOf()

    val uiData = PickContactUIState()

    override fun onCreate() {
        navControls.isVisible       = searchType == PickContactSearchType.NAVIGATION

        navControls.title = sharedString(SR.strings.contacts)

        scope.launch {
            if (PermissionHelper.hasPermission(Permission.Contacts))
                updateAndLoadContacts(this)
        }
    }

    private suspend fun updateAndLoadContacts(coroutineScope: CoroutineScope) {
        uiData.showContactPermissionView.value = false

        showProgress(true)
        loadRequestedContacts()
    }

    fun onRefresh() {
        uiData.refreshing.value = true
    }

    fun resetStateIfNeeded() {
        if (searchControls.searchText.isNotEmpty())
            resetState()
    }

    override fun onSearchTextChanged(search: String) {
        searchInput(search)
    }

    fun onScrolledToTop() {
        uiData.scrollToTop.value = false
    }

    private fun resetState() {
        launchOnMain {
            uiData.contactsList.value   = uiData.contactsList.value.map { contact -> contact.copy(isChecked = false) }.toImmutableList()
            uiData.showNextButton.value = false
            uiData.showSendNew.value    = false
            searchControls.searchText   = ""

        }.invokeOnCompletion {
            if (PermissionHelper.hasPermission(Permission.Contacts))
                loadRequestedContacts(forceScrollTop = true)
        }
    }

    private fun loadRequestedContacts(forceScrollTop: Boolean = false, fromSearch: Boolean = false) {

    }

    private fun getAllContacts(searchText: String, favourites: List<PickContactDataModel>, fromSearch: Boolean = false, forceScrollTop: Boolean = false) {
        getContacts(searchText, favourites).let { contacts ->
            contactsDataModelList = contacts
            showContacts(forceScrollTop, fromSearch)
            showProgress(false)
        }
    }
    private fun checkIsAllowForVersion(): Boolean = true
    fun onDeselectContact(index: Int) {
        uiData.checkedContactsList.value = uiData.checkedContactsList.value.toMutableList().deleteAt(index)
        checkedIdentifiers.removeAt(index)
        showContacts(false)
        uiData.showNextButton.value             = checkedIdentifiers.isNotEmpty()
        uiData.showCheckedContactsList.value    = checkedIdentifiers.isNotEmpty()
    }

    fun onContactClicked(index: Int) {
        if (!checkIsAllowForVersion())
            return

        val chosenContact = uiData.contactsList.value[index]
    }

    private fun showContacts(shouldScroll: Boolean, fromSearch: Boolean = false, isValidInput: Boolean = true) {
        val visualList = mutableListOf<PickContactVisualModel>()

        scope.launch {
            mutex.withLock {
                if (!Preferences.getBoolean(PREFERENCES_ARE_CONTACTS_SYNCED, false) && contactsDataModelList.isNotEmpty())
                    contactsDataModelList.first().sectionName = sharedString(SR.strings.contacts)

                contactsDataModelList
                    .groupBy { it.dataType }
                    .onEach { contactsMap ->
                        contactsMap.value.mapNotNull { contact -> if (uiData.checkedContactsList.value.find { it.identifier == contact.identifier } == null) fromContactRowModel(contact) else null }.also { list ->
                            list.firstOrNull()?.let { first -> first.decorator = ItemDecor(roundedTop = true, label = first.sectionName.ifEmpty { contactsMap.value.first().sectionName }, isOuterLabel = true) }
                            list.lastOrNull()?.let { last -> last.decorator?.let { decor ->  decor.roundedBottom = true } ?: run { last.decorator = ItemDecor(roundedBottom = true) } }
                        }.also { list ->
                            visualList.addAll(list)
                        }
                    }.also {
                        uiData.showNextButton.value  = checkedIdentifiers.isNotEmpty() || visualList.firstOrNull { it.isChecked } != null/* || (contactsDataModelList.isEmpty() && uiState.value.textFieldSearch.isNotEmpty() && searchType == PickContactSearchType.NAVIGATION)*/
                        uiData.textFieldError.value  = false
                        uiData.contactsLoaded.value  = true
                        uiData.scrollToTop.value     = shouldScroll || fromSearch
                        uiData.contactsList.value    = visualList.toImmutableList()
                        uiData.showSendNew.value     = visualList.isEmpty() && isValidInput
                        uiData.sendNewText.value     = searchControls.searchText
                    }
            }
        }
    }

    private fun onContactChosen(contact: PickContactDataModel) {
        hideKeyboard()
        if (onSuccess(arrayListOf(contact)))
            resetState()
    }

    private fun searchInput(searchText: String) {
        val newText = searchText.replace("'", "")
        if (!searchText.equalsIgnoreCase(newText)) {
            showSnackbar(SR.strings.not_allowed_symbol)
            searchControls.searchText = newText
            return
        }

        searchControls.searchText = newText

        if (PermissionHelper.hasPermission(Permission.Contacts)) {
            searchJob?.cancel()
            searchJob = scope.launch {
                loadRequestedContacts(forceScrollTop = false, fromSearch = true)
            }
        }
        else {
             if (searchType == PickContactSearchType.NAVIGATION)
            //uiData.showNextButton.value = newText.isValidPhone
                uiData.showSendNew.value = newText.isValidPhone

        }
    }

    fun onNextButtonClicked() {
        if (!checkIsAllowForVersion())
            return

        launchOnMain {
            if (searchControls.searchText.isNotEmpty() && searchControls.searchText.isValidPhone) {
                val identifier = getNormalizedIdentifier(searchControls.searchText)
                onContactChosen(PickContactDataModel(identifier = identifier, displayName = identifier))
            } else
                showSnackbar(SR.strings.invalid_phone_number)
        }
    }

    private fun getNormalizedIdentifier(identifier: String) = if (identifier.isValidPhone) identifier.normalizedPhoneNumber else identifier
}